import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  UserPlusIcon,
  EnvelopeIcon,
  BuildingOffice2Icon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import ListBoxQueryMultiSelectEmployeesActiveContract from "../components/list_box_query_employee_active_contract";
import CustomTooltipComponent from "./custom_tooltip_component";

const splitEmails = (s) =>
  (s || "")
    .split(/[,;\s]+/)
    .map((t) => t.trim())
    .filter(Boolean);

const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || "").trim());

const uniqLower = (arr) => {
  const seen = new Set();
  const out = [];
  for (const v of arr || []) {
    const key = String(v || "").trim().toLowerCase();
    if (!key) continue;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out;
};

export default function RecipientsField({
  token,
  value,
  initialValue = "",
  onChange,
  placeholder = "Scrivi un'email e premi Invio",
  className = "",

  extractEmployeeEmail = (emp) => emp?.email_auth || emp?.email || null,
  extractEmployeeLabel = (emp) => {
    const full =
      [emp?.first_name, emp?.last_name].filter(Boolean).join(" ").trim() ||
      emp?.label ||
      emp?.name ||
      "";
    return String(full || "").trim();
  },

  disabled = false,
  openDirection,
  isEdit = false,

  // suggested dal genitore (endpoint document-recipients-default)
  companyEmail = "",
  companyName = "Azienda",
  handlers = [], // [{email,label,employee_id,type}] -> SOLO ATTIVI
  othersEmails = "",
  hasShiftsLevel = false,
}) {
  // ---------------- controlled emails ----------------
  const [emails, setEmails] = useState(() => {
    const seed = value !== undefined ? value : initialValue;
    return uniqLower(splitEmails(seed).filter(isEmail));
  });

  const [textValue, setTextValue] = useState("");
  const [commitInvalids, setCommitInvalids] = useState([]);
  const inputRef = useRef(null);
  
  
  // sync quando cambia value dal parent
  useEffect(() => {
    if (value === undefined) return;
    const next = uniqLower(splitEmails(value).filter(isEmail));
    setEmails(next);
    setTextValue("");
    setCommitInvalids([]);
  }, [value]);

  const commitChange = useCallback(
    (nextArray) => {
      const unique = uniqLower(nextArray).filter(isEmail);
      setEmails(unique);
      onChange?.(unique.join(","));
    },
    [onChange]
  );

  const tryCommitFreeText = useCallback(() => {
    const parts = splitEmails(textValue);
    if (!parts.length) {
      setCommitInvalids([]);
      return;
    }

    const valid = parts.filter(isEmail).map((x) => x.toLowerCase());
    const invalid = parts.filter((p) => !isEmail(p));

    if (valid.length) commitChange([...emails, ...valid]);

    setCommitInvalids(invalid);
    if (!invalid.length) setTextValue("");
    else if (valid.length) setTextValue(invalid.join(", "));
  }, [textValue, emails, commitChange]);

  // ---------------- picker dipendenti ----------------
  const stableSelectedEmployees = useMemo(() => [], []);
  const stableEmployeeRolePairs = useMemo(() => [], []);

  // meta per label “Altri destinatari”
  const [othersMeta, setOthersMeta] = useState({});

  const addEmailsFromList = useCallback(
    (list) => {
      const valid = (list || [])
        .map((x) => String(x || "").trim().toLowerCase())
        .filter(isEmail);
      if (valid.length) commitChange([...emails, ...valid]);
    },
    [emails, commitChange]
  );

  const handleEmployeesPicked = useCallback(
    (sel) => {
      const arr = Array.isArray(sel) ? sel : sel ? [sel] : [];
      if (!arr.length) return;

      const pickedEmails = [];
      const metaUpdates = {};

      for (const emp of arr) {
        const email = String(extractEmployeeEmail(emp) || "").trim().toLowerCase();
        if (!isEmail(email)) continue;

        pickedEmails.push(email);

        const label = extractEmployeeLabel(emp);
        if (label) metaUpdates[email] = label;
      }

      if (Object.keys(metaUpdates).length) {
        setOthersMeta((prev) => ({ ...prev, ...metaUpdates }));
      }

      addEmailsFromList(pickedEmails);
    },
    [extractEmployeeEmail, extractEmployeeLabel, addEmailsFromList]
  );

  const removeEmail = useCallback(
    (email) => commitChange(emails.filter((x) => x !== email)),
    [emails, commitChange]
  );

  // ---------------- suggested NORMALIZZATI dal genitore ----------------
  const companyEmailKey = useMemo(
    () => String(companyEmail || "").trim().toLowerCase(),
    [companyEmail]
  );

  const handlersList = useMemo(() => {
    const arr = Array.isArray(handlers) ? handlers : [];
    return arr
      .map((h) => ({
        email: String(h?.email || "").trim().toLowerCase(),
        label: String(h?.label || "").trim() || String(h?.email || "").trim(),
        employee_id: h?.employee_id,
        type: h?.type,
      }))
      .filter((h) => isEmail(h.email));
  }, [handlers, hasShiftsLevel]);

  const handlersEmailSet = useMemo(() => {
    const s = new Set();
    handlersList.forEach((h) => s.add(h.email));
    return s;
  }, [handlersList]);

  const handlersMeta = useMemo(() => {
    const meta = {};
    handlersList.forEach((h) => {
      meta[h.email] = { label: h.label || h.email, type: "handler" };
    });
    return meta;
  }, [handlersList]);

  const othersList = useMemo(() => {
    return uniqLower(splitEmails(othersEmails).filter(isEmail));
  }, [othersEmails]);

  // ---------------- gruppi destinatari (sempre, anche in view) ----------------
  const groups = useMemo(() => {
    const company = [];
    const handlersGroup = [];
    const othersGroup = [];

    for (const m of emails) {
      const key = m.toLowerCase();
      if (companyEmailKey && key === companyEmailKey) company.push(m);
      else if (handlersEmailSet.has(key)) handlersGroup.push(m);
      else othersGroup.push(m);
    }

    return { company, handlers: handlersGroup, others: othersGroup };
  }, [emails, companyEmailKey, handlersEmailSet]);

  // ---------------- suggeriti visibili (solo in edit) ----------------
  const suggestedFiltered = useMemo(() => {
    const emailSet = new Set(emails.map((e) => e.toLowerCase()));

    const companySug =
      companyEmailKey && isEmail(companyEmailKey) && !emailSet.has(companyEmailKey)
        ? { email: companyEmailKey, label: String(companyName || "Azienda").trim() || "Azienda" }
        : null;

    const handlersSug = handlersList.filter((h) => h.email && !emailSet.has(h.email));

    const othersSug = (othersList || [])
      .filter((e) => e && !emailSet.has(e))
      .map((e) => ({ email: e, })); 

    return { company: companySug, handlers: handlersSug, others: othersSug };
  }, [emails, companyEmailKey, companyName, handlersList, othersList]);

  const addSuggestedEmail = useCallback(
    (email) => {
      const e = String(email || "").trim().toLowerCase();
      if (!isEmail(e)) return;
      if (emails.map((x) => x.toLowerCase()).includes(e)) return;
      commitChange([...emails, e]);
    },
    [emails, commitChange]
  );
  
  // ---------------- UI helpers ----------------
  const GroupTitle = ({ icon: Icon, title }) => (
    <div className="flex items-center gap-2 text-xs font-semibold text-gray-600 mb-1">
      <Icon className="w-4 h-4 text-indigo-600" />
      <span>{title}</span>
    </div>
  );

  const SuggestedChip = ({ icon: Icon, label, email }) => (
    <button
      type="button"
      onClick={() => addSuggestedEmail(email)}
      className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1 text-xs text-gray-700 hover:bg-gray-50"
      title="Aggiungi ai destinatari"
      disabled={disabled}
    >
      <Icon className="w-4 h-4 text-indigo-600" />
      <span className="font-semibold">{label}</span>
      <span className="text-gray-500">— {email}</span>
    </button>
  );

  const renderPill = (m) => {
    const key = m.toLowerCase();
    const isCompany = !!companyEmailKey && key === companyEmailKey;

    const rawLabel =
      isCompany
        ? companyName
        : handlersMeta?.[key]?.label
          ? handlersMeta[key].label
          : othersMeta?.[key] || "";

    const normalizedLabel = String(rawLabel || "").trim();
    const displayLabel =
      normalizedLabel && normalizedLabel.toLowerCase() !== key ? normalizedLabel : "";

    return (
      <CustomTooltipComponent
        key={m}
        title={isEdit ? "Clicca per rimuovere" : undefined}
        placement="top"
        arrow
        enterDelay={1000}
      >
        <span>
          <button
            type="button"
            onClick={() => {
              if (!isEdit) return;
              removeEmail(m);
            }}
            disabled={!isEdit || disabled}
            className={`group inline-flex items-center gap-2 rounded-xl px-3 py-1 text-xs border text-left
              ${isEdit && !disabled ? "cursor-pointer hover:opacity-90" : "cursor-default"}
              bg-indigo-50 text-indigo-700 border-indigo-200 focus:ring-indigo-400 focus:border-2 focus:border-indigo-400`}
          >
            <span className="leading-tight">
              {displayLabel ? (
                <span className="block uppercase text-indigo-700 text-xs font-medium">
                  {displayLabel}
                  {" - "}
                  <span className="lowercase">{m}</span>
                </span>
              ) : (
                <span>{m}</span>
              )}
            </span>
          </button>
        </span>
      </CustomTooltipComponent>
    );
  };

  const hasSuggested =
    !!suggestedFiltered.company ||
    (suggestedFiltered.handlers?.length || 0) > 0 ||
    (suggestedFiltered.others?.length || 0) > 0;

  return (
    <div>
      <div className={`${disabled ? "pointer-events-none" : ""} ${className}`}>
        {/* SUGGERITI (solo edit) */}
        {isEdit && hasSuggested && (
          <div className="mb-2 px-3">
            <div className="text-xs font-semibold text-gray-700 mb-2">Suggeriti</div>

            <div className="flex flex-wrap gap-2">
              {suggestedFiltered.company && (
                <SuggestedChip
                  icon={BuildingOffice2Icon}
                  label={suggestedFiltered.company.label || "Azienda"}
                  email={suggestedFiltered.company.email}
                />
              )}

              {(suggestedFiltered.handlers || []).map((h) => (
                <SuggestedChip
                  key={h.email}
                  icon={UsersIcon}
                  label={h.label || "Gestore"}
                  email={h.email}
                />
              ))}

              {(suggestedFiltered.others || []).map((o) => (
                <SuggestedChip
                  key={o.email}
                  icon={EnvelopeIcon}
                  label={"Altro"}
                  email={o.email}
                />
              ))}
            </div>
          </div>
        )}

        {/* EDIT CONTROLS */}
        {isEdit && (
          <>
           {hasShiftsLevel && (
              <div className="mb-3 px-2">
                  <div className="grid grid-cols-1 sm:grid-cols-[220px,1fr] sm:items-start gap-2 sm:gap-3">
                    <div className="flex items-center pt-4 gap-2">
                      <UserPlusIcon className="w-5 h-5 text-indigo-500" />
                      <span className="text-sm text-gray-600 whitespace-nowrap">Aggiungi da dipendenti</span>
                    </div>

                    <div className='w-full [&_button]:h-10 [&_button]:w-full [&_button]:text-left [&_button]:rounded-md [&_button]:border [&_button]:border-gray-200 [&_button]:bg-white [&_button]:px-3 [&_button:focus]:outline-none [&_button:focus]:ring-2 [&_button:focus]:ring-indigo-200 [&_button:focus]:border-indigo-400 [&_svg]:h-5 [&_svg]:w-5'>
                      <ListBoxQueryMultiSelectEmployeesActiveContract
                        viewEmail
                        token={token}
                        selectedEmployees={stableSelectedEmployees}
                        employeeRolePairs={stableEmployeeRolePairs}
                        onChange={handleEmployeesPicked}
                        placeholder="Cerca e seleziona dipendenti…"
                        filterBy="hr_active"
                        pageSize={8}
                        openDirection={openDirection}
                        positionOptions="relative"
                        disabled={disabled}
                      />
                    </div>
                  </div>
              </div>
            )}

            <div className="px-2">
              <div className="grid grid-cols-1 sm:grid-cols-[220px,1fr] sm:items-center gap-2 sm:gap-3">
                <div className="flex items-center gap-2">
                  <EnvelopeIcon className="w-5 h-5 text-indigo-500" />
                  <span className="text-sm text-gray-600 whitespace-nowrap">{hasShiftsLevel ? 'Inserisci manualmente' : 'Inserisci email'}</span>
                </div>

                <div>
                  <input
                    ref={inputRef}
                    type="text"
                    value={textValue}
                    onChange={(e) => {
                      setTextValue(e.target.value);
                      if (commitInvalids.length) setCommitInvalids([]);
                    }}
                    onKeyDown={(e) => {
                      if (disabled) return;
                      if (e.key === "Enter" || e.key === ",") {
                        e.preventDefault();
                        tryCommitFreeText();
                      }
                    }}
                    onBlur={() => {
                      if (!textValue) return;
                      tryCommitFreeText();
                    }}
                    placeholder={placeholder}
                    className={`w-full h-10 px-3 text-sm text-gray-800 border rounded-md outline-none shadow-md
                      ${
                        commitInvalids.length
                          ? "border-red-300 focus:border-red-400 focus:ring-2 focus:ring-red-200"
                          : "border-gray-200 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                      }`}
                    disabled={disabled}
                  />
                  {commitInvalids.length > 0 && (
                    <p className="mt-1 text-xs text-red-600">Formato non valido: {commitInvalids.join(", ")}</p>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
        
        {/* DESTINATARI */}
        {emails.length > 0 && (
          <div className={`${isEdit ? "mt-2" : "border border-gray-300 p-2"} space-y-3 px-3 py-2 bg-white rounded-md border border-gray-200 mx-2`}>
            {groups.company.length > 0 && (
              <div>
                <GroupTitle icon={BuildingOffice2Icon} title="Azienda" />
                <div className="flex flex-wrap gap-2">{groups.company.map(renderPill)}</div>
              </div>
            )}

            {groups.handlers.length > 0 && (
              <div>
                <GroupTitle icon={UsersIcon} title="Gestori" />
                <div className="flex flex-wrap gap-2">{groups.handlers.map(renderPill)}</div>
              </div>
            )}

            {groups.others.length > 0 && (
              <div>
                <GroupTitle icon={EnvelopeIcon} title="Altri destinatari" />
                <div className="flex flex-wrap gap-2">{groups.others.map(renderPill)}</div>
              </div>
            )}
          </div>
        )}
      </div>

      <input type="hidden" name="notification_email" value={emails.join(",")} readOnly />
    </div>
  );
}